<template lang="html">
  <v-card
    :key="routeTitle"
    flat
  >
    <page-bar
      :title="routeTitle"
      :context-menu="routeContextMenu"
      :loading="isDataLoading"
    >
      <template slot="bar-right">
        <!-- PAGE BAR BUTTONS -->
        <v-btn
          text
          icon
          :disabled="isTodayOrOlder"
          @click="backOneDay()"
        >
          <v-icon dark>
            mdi-chevron-left
          </v-icon>
        </v-btn>
        <v-btn
          text
          icon
          @click="forwardOneDay()"
        >
          <v-icon dark>
            mdi-chevron-right
          </v-icon>
        </v-btn>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              @click="loadData(false)"
            >
              <v-icon dark>
                {{ refreshIcon }}
              </v-icon>
            </v-btn>
          </template>
          <span>Refresh Data</span>
        </v-tooltip>
      </template>
    </page-bar>
    <api-errors :errors="errors" />

    <!-- Event Itinerary Dialog -->
    <v-dialog
      v-model="showEventItineraryDialog"
      :max-width="800"
      scrollable
    >
      <v-card>
        <v-card-title>
          <div class="headline">
            {{ restrictionObject.name }} Itinerary
          </div>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-layout wrap>
            <v-flex
              v-if="reservationNoChildren"
              class="xs12 sm6 md6"
            >
              <v-text-field
                prepend-icon="mdi-cancel"
                :value="reservationNoChildren"
                :readonly="true"
              />
            </v-flex>
            <v-flex
              v-if="reservationDressCode"
              class="xs12 sm6 md6"
            >
              <v-text-field
                prepend-icon="mdi-tie"
                label="Dress Code"
                :value="reservationDressCode.name"
                :readonly="true"
              />
            </v-flex>
            <v-flex
              v-if="reservationMusicalStyle"
              class="xs12 sm6 md6"
            >
              <v-text-field
                prepend-icon="mdi-music"
                label="Muscial Style"
                :value="reservationMusicalStyle.name"
                :readonly="true"
              />
            </v-flex>
            <v-flex
              v-if="reservationTheme"
              class="xs12 sm6 md6"
            >
              <v-text-field
                prepend-icon="fa-theater-masks"
                label="Theme"
                :value="reservationTheme"
                :readonly="true"
              />
            </v-flex>
            <v-flex
              v-if="reservationExtras"
              class="xs12 sm6 md6"
            >
              <v-text-field
                prepend-icon="mdi-message"
                label="Extras"
                :value="reservationExtras"
                :readonly="true"
              />
            </v-flex>
          </v-layout>
          <v-layout
            v-if="restrictionObject.onlineMessage"
            wrap
            v-html="restrictionObject.onlineMessage"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="red--text darken-1"
            text
            @click="showEventItineraryDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-layout>
      <v-flex class="xs6 sm3 md3 px-3">
        <date-picker
          v-model.trim="date"
          label="Selected Date"
          :events="dateHasBlocker"
          :event-color="dateColor"
          :allowed-dates="allowedPickerDates"
          required
        />
      </v-flex>
    </v-layout>

    <v-layout wrap>
      <v-flex :class="panelClass">
        Dining Summary for {{ dateDisplay }}
        <table class="ft-dashboard-table">
          <thead>
            <tr :class="headerClass">
              <th>Location</th>
              <th>Meal Period</th>
              <th>Time</th>
              <th>Res./Covers</th>
              <th>W/L</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="mealPeriodItem in dailySummary.filter(mp => !mp._hide)"
              :key="mealPeriodItem.id"
              :class="{ftIsSelected: mealPeriodItem.id === selectedMealPeriodId}"
              :style="mealPeriodItem._hasBlocked ? 'color: ' + (mealPeriodItem._fullyBlocked ? $ftCurrentTheme.error : $ftCurrentTheme.warning) : ''"
            >
              <td>{{ mealPeriodItem.locationName }}</td>
              <td><a @click="loadMealPeriodDetails(mealPeriodItem.id)">{{ mealPeriodItem.name }}</a></td>
              <td class="ftDateSpans">
                <span>{{ $ftPrettyTime(mealPeriodItem.startTime) }}</span> - <span>{{ $ftPrettyTime(mealPeriodItem.endTime) }}</span>
              </td>
              <td>
                <span v-if="mealPeriodItem._covers || mealPeriodItem._coversMerged">
                  <a
                    @click="gotoMealPeriod(mealPeriodItem)"
                  >{{ mealPeriodItem._resCount || '-' }} / {{ mealPeriodItem._covers || '-' }}</a>
                  <span v-if="mealPeriodItem._overlaps && mealPeriodItem._coversMerged !== mealPeriodItem._covers"> (<a
                    @click="gotoMealPeriod(mealPeriodItem, mealPeriodItem._overlapIds)"
                  >{{ mealPeriodItem._resCountMerged || '-' }} / {{ mealPeriodItem._coversMerged || '-' }}</a>)
                  </span>
                </span>
                <span v-else>-</span>
              </td>
              <td>
                <span v-if="mealPeriodItem._waitlisted || mealPeriodItem._waitlistedMerged">
                  <a
                    @click="gotoMealPeriodWaitlist(mealPeriodItem)"
                  >{{ mealPeriodItem._waitlisted || '-' }}</a>
                  <span v-if="mealPeriodItem._overlaps && mealPeriodItem._waitlistedMerged !== mealPeriodItem._waitlisted"> (<a
                    @click="gotoMealPeriodWaitlist(mealPeriodItem, mealPeriodItem._overlapIds)"
                  >{{ mealPeriodItem._waitlistedMerged || '-' }}</a>)
                  </span>
                </span>
                <span v-else>-</span>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="mealPeriod">
          {{ mealPeriod.locationName }} - {{ mealPeriod.name }} on {{ dateDisplay }}
          <span class="ft-dashboard-options"><a @click="gotoMealPeriod(mealPeriod)">View Registrants</a></span>
          <table class="ft-dashboard-table">
            <thead>
              <tr :class="headerClass">
                <th>Time</th>
                <th>Covers (new:total)</th>
                <th v-if="mealPeriod.tableConfigId">
                  Tables
                </th>
                <th v-if="mealPeriod.tableConfigId">
                  Open Chairs (min:max)
                </th>
                <th
                  v-for="(period, pi) in firstMealPeriodSeatingPeriods"
                  :key="pi"
                >
                  {{ period.name }} per {{ period.periodName }}
                </th>
                <th>W/L</th>
                <th v-if="mealPeriod._hasBlocked">
                  &nbsp;
                </th>
                <th v-if="mealPeriod._overlaps">
                  Override
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(seating, si) in mealPeriodSeatings"
                :key="si"
                :style="seating.blockReason ? 'color: ' + (seating.disabled ? $ftCurrentTheme.error : $ftCurrentTheme.warning) : ''"
              >
                <td v-if="allowDiningReservation(seating)">
                  <a @click="newDiningReservation(seating)">{{ seating.displayTime }}</a>
                </td>
                <td v-else>
                  {{ seating.displayTime }}
                </td>
                <td>
                  {{ (seating.covers || 0) + ' : ' + periodCountsByPartySize(seating, 2).covers }}
                </td>
                <td
                  v-if="mealPeriod.tableConfigId"
                  :style="
                    periodCountsByPartySize(seating, 2).totalTables > 0 && periodCountsByPartySize(seating, 2).usedTables >= periodCountsByPartySize(seating, 2).totalTables ?
                      'color: ' +
                      (periodCountsByPartySize(seating, 2).usedTables > periodCountsByPartySize(seating, 2).totalTables ?
                        $ftCurrentTheme.error :
                        $ftCurrentTheme.warning) :
                      ''"
                >
                  {{ periodCountsByPartySize(seating, 2).usedTables + ' / ' + periodCountsByPartySize(seating, 2).totalTables }}
                </td>
                <td v-if="mealPeriod.tableConfigId">
                  {{ periodCountsByPartySize(seating, 2).minAvailChairs + ' : ' + periodCountsByPartySize(seating, 2).maxAvailChairs }}
                </td>
                <td
                  v-for="(period, pi) in seating.seatingCounts"
                  :key="pi"
                  :style="period.periodCovers >= period.max ? 'color: ' + (period.periodCovers > period.max ? $ftCurrentTheme.error : $ftCurrentTheme.warning) : ''"
                >
                  {{ period.periodCovers ? period.periodCovers + ' / ' + period.max : '-' }}
                </td>
                <td>
                  {{ seating.waitlistCovers ? seating.waitlistCovers : '-' }}
                </td>
                <td v-if="mealPeriod._hasBlocked">
                  <span v-if="seating.blockReason">{{ seating.blockReason }}</span>
                </td>
                <td v-if="mealPeriod._overlaps">
                  <span v-if="seating.mealPeriodId !== mealPeriod.id">{{ (mealPeriodMap[seating.mealPeriodId] || {}).name }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-flex>


      <v-flex :class="panelClass">
        Scheduled Events for {{ dateDisplay }}
        <table class="ft-dashboard-table">
          <thead>
            <tr :class="headerClass">
              <th>Event Name</th>
              <th>Date</th>
              <th>Time</th>
              <th>Covers</th>
              <th>W/L</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="eventItem in todaysEvents"
              :key="eventItem.id"
              :class="{'ft-strikethrough': eventItem.cancelled, ftIsSelected: eventItem.id === selectedEventId}"
            >
              <td><a @click="loadEventDetails(eventItem.id)">{{ eventItem.name }}</a></td>
              <td>{{ $ftPrettyDate(eventItem.date) }}</td>
              <td>{{ $ftPrettyTime(eventItem.startDateTime) }}</td>
              <td>
                <a
                  v-if="eventItem._covers"
                  @click="gotoEvent(eventItem)"
                >{{ eventItem._covers }} / {{ eventItem._maxSeating }} ({{ Math.round(eventItem._covers / eventItem._maxSeating * 100) }}%)</a><span v-else>-</span>
              </td>
              <td>
                <a
                  v-if="eventItem._waitlisted"
                  @click="gotoEventWaitlist(eventItem)"
                >{{ eventItem._waitlisted || '-' }}</a><span v-else>-</span>
              </td>
            </tr>
          </tbody>
        </table>

        Upcoming Events
        <span class="ft-dashboard-options"><a @click="loadUpcomingEvents(30)">0-30 days</a> <a @click="loadUpcomingEvents('all')">All</a></span>
        <table class="ft-dashboard-table">
          <thead>
            <tr :class="headerClass">
              <th>Event Name</th>
              <th>Date</th>
              <th>Time</th>
              <th>Covers</th>
              <th>W/L</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="eventItem in upcomingEvents"
              :key="eventItem.id"
              :class="{'ft-strikethrough': eventItem.cancelled, ftIsSelected: eventItem.id === selectedEventId}"
            >
              <td><a @click="loadEventDetails(eventItem.id)">{{ eventItem.name }}</a></td>
              <td>{{ $ftPrettyDate(eventItem.date) }}</td>
              <td>{{ $ftPrettyTime(eventItem.startDateTime) }}</td>
              <td>
                <a
                  v-if="eventItem._covers"
                  @click="gotoEvent(eventItem)"
                >{{ eventItem._covers }} / {{ eventItem._maxSeating }} ({{ Math.round(eventItem._covers / eventItem._maxSeating * 100) }}%)</a><span v-else>-</span>
              </td>
              <td>
                <a
                  v-if="eventItem._waitlisted"
                  @click="gotoEventWaitlist(eventItem)"
                >{{ eventItem._waitlisted || '-' }}</a><span v-else>-</span>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="event">
          {{ event.name }} on {{ $ftPrettyDate(event.date) }}
          <span class="ft-dashboard-options"><a @click="gotoEvent(event)">View Registrants</a></span>
          <span
            v-if="hasEventItinerary"
            class="ft-dashboard-options"
          ><a @click="showEventItineraryDialog = true">Show Itinerary</a></span>
          <table class="ft-dashboard-table">
            <thead>
              <tr :class="headerClass">
                <th>Location</th>
                <th>Time</th>
                <th>Covers (new:total)</th>
                <th>Tables</th>
                <th>Open Chairs (min:max)</th>
                <th>W/L</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(seating, si) in event.fullSeatings"
                :key="si"
              >
                <td>{{ seating.label }}</td>
                <td v-if="allowEventReservation(seating)">
                  <a @click="newEventReservation(seating)">{{ seating.displayTime }}</a>
                </td>
                <td v-else>
                  {{ seating.displayTime }}
                </td>
                <td>{{ seating._covers }} : {{ periodCountsByPartySize(seating, 2).covers }} / {{ seating._maxSeating }} ({{ Math.round( periodCountsByPartySize(seating, 2).covers / seating._maxSeating * 100) }}%)</td>
                <td
                  :style="
                    periodCountsByPartySize(seating, 2).totalTables > 0 && periodCountsByPartySize(seating, 2).usedTables >= periodCountsByPartySize(seating, 2).totalTables ?
                      'color: ' +
                      (periodCountsByPartySize(seating, 2).usedTables > periodCountsByPartySize(seating, 2).totalTables ?
                        $ftCurrentTheme.error :
                        $ftCurrentTheme.warning) :
                      ''"
                >
                  {{ periodCountsByPartySize(seating, 2).usedTables + ' / ' + periodCountsByPartySize(seating, 2).totalTables }}
                </td>
                <td>{{ periodCountsByPartySize(seating, 2).minAvailChairs + ' : ' + periodCountsByPartySize(seating, 2).maxAvailChairs }}</td>
                <td>{{ seating._waitlisted || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-flex>
    </v-layout>
  </v-card>
</template>

<script>
import {helpers, navRoute, errorHandler, apiHelpers} from '@/ft-vue-core'
import moment from 'moment'

const {/* setCount, */ buildUuid} = helpers

const DEFAULT_RANGE = 30

export default {
  name: 'Dashboard',
  mixins: [apiHelpers, navRoute, errorHandler],
  data: function () {
    return {
      date: moment().format('YYYY-MM-DD'),
      dateDisplay: null,
      todaysEvents: [],
      upcomingEvents: [],
      seatingTimes: [],
      dailySummary: [],
      // reservationCount: [],
      coverCount: [],
      waitlistCoverCount: [],
      range: DEFAULT_RANGE,
      eventsWithSeatings: [],
      mealPeriodsWithSeatings: [],
      dressCodes: [],
      musicalStyles: [],
      eventSeatings: [],
      showEventItineraryDialog: false,
      reservationAllowStart: moment(),
      reservationAllowEnd: moment(),
      eventAllowStart: moment(),
      availableMealPeriodDates: [],
      selectedEventId: null,
      selectedMealPeriodId: null,
      reservationOpen: false,
      autoRefresh: false,
      refreshTimer: null,
      refreshFrame: null,
      refreshTime: 300000, // refresh every 5 min
      lastDateWasToday: false,
    }
  },
  computed: {
    refreshIcon () {
      // return this.autoRefresh ? 'mdi-sync-circle' : 'mdi-refresh'
      return 'mdi-refresh'
    },
    headerClass () {
      return 'info'
    },
    usesMealPeriods () {
      return this.$ftCS.DINING_USES_RESERVATIONS
    },
    usesEvents () {
      return this.$ftCS.DINING_USES_EVENTS
    },
    panelCount () {
      var count = 0
      if (this.usesMealPeriods) count ++
      if (this.usesEvents) count ++
      return count
    },
    panelClass () {
      return ['sm12 md6', 'lg' + 12 / (this.panelCount || 1), 'px-3 ft-dashboard-section-label']
    },
    event () {
      return this.eventsWithSeatings[0] || null
    },
    mealPeriod () {
      return this.mealPeriodsWithSeatings[0] || null
    },
    mealPeriodMap () {
      return this.dailySummary.reduce((a, mp) => {
        a[mp.id] = mp
        return a
      }, {})
    },
    firstMealPeriodSeatingPeriods () {
      return (this.mealPeriodSeatings[0] ? this.mealPeriodSeatings[0] : {seatingCounts: []}).seatingCounts
    },
    mealPeriodSeatings () {
      const mealPeriod = this.mealPeriod
      if (!mealPeriod) return []
      return mealPeriod.seatings
    },
    selectableMealPeriodDates () {
      return ((this.availableMealPeriodDates || [])[0] || {}).dates || []
    },
    blockedMealPeriodDates () {
      return ((this.availableMealPeriodDates || [])[0] || {}).blockedDates || []
    },
    partialMealPeriodDates () {
      return ((this.availableMealPeriodDates || [])[0] || {}).partialDates || []
    },
    eventMealPeriodDates () {
      return ((this.availableMealPeriodDates || [])[0] || {}).eventDates || []
    },
    isTodayOrOlder () {
      return moment(this.date).isSameOrBefore(this.$ftAPI().clubMoment(), 'day')
    },
    isToday () {
      return moment(this.date).isSame(this.$ftAPI().clubMoment(), 'day')
    },
    isPast () {
      return moment(this.date).isBefore(this.$ftAPI().clubMoment(), 'day')
    },
    reservationDressCode () {
      const restrictionObject = this.restrictionObject
      if (!restrictionObject.dressCodeId) return null
      return this.dressCodeMap[restrictionObject.dressCodeId]
    },
    reservationMusicalStyle () {
      const restrictionObject = this.restrictionObject
      if (!restrictionObject.musicalStyleId) return null
      return this.musicalStyleMap[restrictionObject.musicalStyleId]
    },
    reservationNoChildren () {
      const restrictionObject = this.restrictionObject
      if (restrictionObject.childrenAllowed) return null
      return 'No Children'
    },
    reservationTheme () {
      const restrictionObject = this.restrictionObject
      if (!restrictionObject.theme) return null
      return restrictionObject.theme
    },
    reservationExtras () {
      const restrictionObject = this.restrictionObject
      if (!restrictionObject.extras) return null
      return restrictionObject.extras
    },
    restrictionObject () {
      const event = this.event
      if (event) {
        return event
      }
      return {}
    },
    hasEventItinerary () {
      const restrictionObject = this.restrictionObject
      return restrictionObject.childrenAllowed === false || restrictionObject.musicalStyleId || restrictionObject.dressCodeId || restrictionObject.theme || restrictionObject.extras || restrictionObject.onlineMessage
    },
    dressCodeMap () {
      return this.dressCodes.reduce((result, i) => {
        result[i.id] = i
        return result
      }, {})
    },
    musicalStyleMap () {
      return this.musicalStyles.reduce((result, i) => {
        result[i.id] = i
        return result
      }, {})
    }
  },
  watch: {
    date (newVal, oldVal) {
      if (newVal === oldVal) return
      // reload content that's based upon the selected date
      const query = Object.assign({}, this.$route.query)
      const qd = query.d
      const clubDate = this.$ftAPI().clubMoment().format('YYYY-MM-DD')
      delete query.d
      this.lastDateWasToday = newVal === clubDate
      if (!this.lastDateWasToday) {
        query.d = newVal
      }
      query.sxp = '1'
      if (query.d != qd) {
        this.$router.push({
          query: query
        })
      }
      this.loadData(true)
    },
    $route () {
      this.checkRoute()
    }
  },
  created () {
    this.date = this.$ftAPI().clubMoment().format('YYYY-MM-DD')
    this.lastDateWasToday = true
  },
  mounted () {
    this.autoRefresh = true
    this.clearRefresh()
    this.checkRoute(true)
    this.loadData()
  },
  beforeUnmount () {
    this.autoRefresh = false
    this.clearRefresh()
  },
  beforeDestroy () {
    this.autoRefresh = false
    this.clearRefresh()
  },
  // beforeRouteUpdate (to, from, next) {
  //   this.autoRefresh = false
  //   clearTimeout(this.refreshTimer)
  //   next(vm => {
  //     vm.autoRefresh = true
  //     vm.scheduleNextRefresh()
  //   })
  // },
  // beforeRouteEnter (to, from, next) {
  //   // clearTimeout(this.refreshTimer)
  //   next(vm => {
  //     vm.autoRefresh = true
  //     vm.scheduleNextRefresh()
  //   })
  // },
  beforeRouteLeave (to, from, next) {
    this.autoRefresh = false
    this.clearRefresh()
    next()
  },
  methods: {
    clearRefresh () {
      clearTimeout(this.refreshTimer)
      cancelAnimationFrame(this.refreshFrame)
      this.refreshFrame
    },
    scheduleNextRefresh () {
      this.clearRefresh()
      if (!this.autoRefresh) return
      this.refreshTimer = setTimeout(() => {
        this.refreshFrame = requestAnimationFrame(() => this.doNextRefresh())
      }, this.refreshTime)
    },
    doNextRefresh () {
      if (!this.autoRefresh) return
      const clubDate = this.$ftAPI().clubMoment().format('YYYY-MM-DD')
      if (this.lastDateWasToday && this.date !== clubDate) {
        // Day has changed
        this.date = clubDate
      } else {
        this.loadData(false)
      }
    },
    hasFullPeriod (seating) {
      return Object.values(seating.seatingCounts || {}).find(p => p.periodCovers >= p.max)
    },
    seatingCounts (seating, period) {
      if (!seating.seatingCounts) return {periodCovers: 0, max: 0}
      return seating.seatingCounts[period] || {periodCovers: 0, max: 0}
    },
    checkRoute (suppressLoad) {
      var doLoad = false
      if (this.$route.query.ocrLId === undefined && this.reservationOpen) {
        // probably just closed a reservation
        this.reservationOpen = false
        doLoad = true
      }
      const mealPeriodId = this.$route.query.mpId ? parseInt(this.$route.query.mpId, 10) : null
      const eventId = this.$route.query.eId ? parseInt(this.$route.query.eId, 10) : null
      const date = (this.$route.query.d ? moment(this.$route.query.d) : this.$ftAPI().clubMoment()).format('YYYY-MM-DD')
      const rawQueryRange = this.$route.query.eRg || DEFAULT_RANGE
      const parsedQueryRange = parseInt(rawQueryRange, 10)
      const queryRange = rawQueryRange === 'all' || isNaN(parsedQueryRange) || parsedQueryRange < 1 ? 'all' : parsedQueryRange
      if (mealPeriodId !== this.selectedMealPeriodId || eventId !== this.selectedEventId || date !== this.date || queryRange !== this.range) {
        this.selectedMealPeriodId = mealPeriodId
        this.selectedEventId = eventId
        this.date = date
        this.range = queryRange
        doLoad = true
      }
      if (!suppressLoad && doLoad) this.loadData()
    },
    allowDiningReservation (seating) {
      const seatingDateTime = moment(seating.date + ' ' + seating.time)
      return !seating.disabled &&
        seatingDateTime.isSameOrAfter(this.reservationAllowStart, 'day') &&
        seatingDateTime.isSameOrBefore(this.reservationAllowEnd, 'day') &&
        this.$ftAccess.manageReservations
    },
    allowEventReservation (seating) {
      const seatingDateTime = moment(seating.date + ' ' + seating.time)
      return !seating.disabled &&
        seatingDateTime.isSameOrAfter(this.eventAllowStart, 'day') &&
        this.$ftAccess.manageReservations
    },
    newDiningReservation (seating) {
      this.reservationOpen = true
      const query = Object.assign({}, this.$route.query)
      this.$router.push({
        query: {
          ...query,
          ocrId: '',
          ocrLId: seating.locationId,
          ocrT: seating.time,
          ocrD: seating.date
        }
      })
    },
    newEventReservation (seating) {
      this.reservationOpen = true
      const query = Object.assign({}, this.$route.query)
      this.$router.push({
        query: {
          ...query,
          ocrId: '',
          ocrLId: seating.locationId,
          ocrT: seating.time,
          ocrEventId: seating.eventId
        }
      })
    },
    dateColor (date) {
      return this.blockedMealPeriodDates[date] ? 'error' : this.partialMealPeriodDates[date] ? 'warning' : this.eventMealPeriodDates[date] ? 'info' : 'black'
    },
    dateHasBlocker (date) {
      return !!this.blockedMealPeriodDates[date] || !!this.partialMealPeriodDates[date] || !!this.eventMealPeriodDates[date]
    },
    allowedPickerDates (date) {
      const selectableMealPeriodDates = this.selectableMealPeriodDates
      return !!(selectableMealPeriodDates[date] && selectableMealPeriodDates[date] && selectableMealPeriodDates[date][0]) || this.eventMealPeriodDates[date]
    },
    backOneDay () {
      this.date = moment(this.date).subtract(1, 'days').format('YYYY-MM-DD')
    },
    forwardOneDay () {
      this.date = moment(this.date).add(1, 'days').format('YYYY-MM-DD')
    },
    getSeatingMax (seating) {
      const sc = Object.values(seating.seatingCounts || {}).find(s => s.countType === 'SEATING')
      if (sc) {
        return sc
      } else {
        return {max: 0, memberMax: 0}
      }
    },
    gotoMealPeriod (mp, ids) {
      ids = ids && ids.map(id => parseInt(id, 10)).filter(i => !isNaN(i))
      if (ids && ids.length) {
        this.$router.push({path: '/reservations/filter', query: {mpIds: JSON.stringify(ids), date: this.date, searchId: buildUuid()}})
      } else {
        this.$router.push({path: '/reservations/filter', query: {mpId: mp.id, date: this.date, searchId: buildUuid()}})
      }
    },
    gotoMealPeriodWaitlist (mp, ids) {
      ids = ids && ids.map(id => parseInt(id, 10)).filter(i => !isNaN(i))
      if (ids && ids.length) {
        this.$router.push({path: '/reservations/filter', query: {mpIds: JSON.stringify(ids), date: this.date, waitlist: '', searchId: buildUuid()}})
      } else {
        this.$router.push({path: '/reservations/filter', query: {mpId: mp.id, date: this.date, waitlist: '', searchId: buildUuid()}})
      }
    },
    gotoEvent (event) {
      this.$router.push({path: '/reservations/filter', query: {eventName: event.name, date: event.date, searchId: buildUuid()}})
    },
    gotoEventWaitlist (event) {
      this.$router.push({path: '/reservations/filter', query: {eventName: event.name, date: event.date, waitlist: '', searchId: buildUuid()}})
    },
    loadMealPeriodDetails (mealPeriodId) {
      if (this.selectedMealPeriodId === mealPeriodId) return
      this.selectedMealPeriodId = mealPeriodId
      const query = Object.assign({}, this.$route.query)
      query.sxp = '1'
      this.$router.push({
        query: {
          ...query,
          mpId: mealPeriodId
        }
      })
      this.$ftAPI().get(this.addResourceErrorHandler(this.mealPeriodSummaryCommand(mealPeriodId)))
    },
    loadEventDetails (eventId) {
      if (this.selectedEventId === eventId) return
      this.selectedEventId = eventId
      const query = Object.assign({}, this.$route.query)
      query.sxp = '1'
      this.$router.push({
        query: {
          ...query,
          eId: eventId
        }
      })
      this.$ftAPI().get(this.addResourceErrorHandler(this.eventSummaryCommand(eventId)))
    },
    loadDailySummary () {
      this.$ftAPI().get(this.addResourceErrorHandler(this.dailySummaryCommand()))
    },
    loadUpcomingEvents (range) {
      if (this.range === range) return
      this.range = range
      const query = Object.assign({}, this.$route.query)
      delete query.eRg
      query.sxp = '1'
      if (range !== DEFAULT_RANGE) {
        query.eRg = range
      }
      this.$router.push({
        query: query
      })
      this.$ftAPI().get(this.addResourceErrorHandler(this.upcomingEventsCommand(range)))
    },
    eventsFilter (range) {
      const filter = [{field: 'date', operator: 'GREATER_THAN', values: [this.$ftAPI().clubMoment().format('YYYY-MM-DD')]}]
      range = range === 'all' ? 0 : parseInt(range, 10)
      if (range > 0) filter.push({field: 'date', operator: 'LESS_OR_EQUAL', values: [this.$ftAPI().clubMoment().add(range, 'd').format('YYYY-MM-DD')]})
      return filter
    },
    mealPeriodSummaryCommand (mealPeriodId) {
      return {
        command: 'diningMealPeriod',
        load: (mealPeriods) => {
          // mealPeriods.forEach(mp => {
          //   console.log('found mealPeriod', mp)
          // })
          const mp = mealPeriods[0]
          if (mp) {
            let hasBlocked = false
            let overlaps = false
            mp.seatings.forEach(s => {
              if (s.blockReason) hasBlocked = true
              if (s.mealPeriodId !== mp.id) overlaps = true
            })
            mp._overlaps = overlaps
            mp._hasBlocked = hasBlocked
          }
          this.mealPeriodsWithSeatings = mealPeriods
        },
        loading: this.setIsLoading,
        params: {
          include_disabled: false,
          include_fields: ['locationName'],
          target_date: this.date,
          order_by: ['locationName'],
          filter: [{field: 'id', operator: 'EQUAL', values: [mealPeriodId]}],
          filter_methods: {mergeSeatings: [true]},
        }
      }
    },
    eventSummaryCommand (eventId) {
      return [
        {command: 'diningDressCode', load: this.dressCodes},
        {command: 'diningMusicalStyle', load: this.musicalStyles},
        {
          command: 'diningEvent',
          load: (events) => {
            events.forEach(e => {
              e.fullSeatings.forEach(fs => {
                const sm = this.getSeatingMax(fs)
                fs._maxSeating = sm.max
                fs._memberMax = sm.memberMax
                fs._covers = fs.covers || 0
                fs._waitlisted = fs.waitlistCovers || 0
              })
            })
            this.eventsWithSeatings = events
          },
          loading: this.setIsLoading,
          params: {
            children: true,
            include_fields: ['fullSeatings', 'onlineMessage'],
            filter: [{field: 'id', operator: 'EQUAL', values: [eventId]}]
          }
        }
      ]
    },
    // reservationCountsCommand (mpList) {
    //   return {
    //     command: 'diningReservation',
    //     loading: this.setIsLoading,
    //     load: (reservations) => {
    //       const coverCount = {}
    //       const reservationCount = {}
    //       const waitlistCoverCount = {}
    //       reservations.forEach(r => {
    //         const mpId = r.mealPeriodId
    //         if (r.waitlist) {
    //           setCount(waitlistCoverCount, mpId, r.covers)
    //         } else {
    //           setCount(reservationCount, mpId, 1)
    //           setCount(coverCount, mpId, r.covers)
    //         }
    //       })
    //       this.coverCount = coverCount
    //       this.reservationCount = reservationCount
    //       this.waitlistCoverCount = waitlistCoverCount
    //       // console.log('counts', this.reservationCount, this.coverCount)
    //       // console.log('reservations', reservations)
    //     },
    //     params: {
    //       filter: [
    //         {field: 'mealPeriodId', operator: 'IN', values: mpList},
    //         {field: 'dateTime', operator: 'BETWEEN', values: [this.date + ' 00:00:00', this.date + ' 23:59:59']},
    //         {field: 'cancelled', operator: 'IS_NULL'}
    //       ]
    //     }
    //   }
    // },
    dailySummaryCommand () {
      return {
        command: 'diningMealPeriod',
        loading: this.setIsLoading,
        load: (mealPeriods) => {
          let mpList = []
          mealPeriods.forEach(mp => {
            let covers = 0
            let coversMerged = 0
            let resCount = 0
            let resCountMerged = 0
            let waitlisted = 0
            let waitlistedMerged = 0
            let hasEnabled = false
            let hasBlocked = false
            let hide = false // true
            let overlaps = false
            let overlapIds = {}
            mpList.push(mp.id)
            mp.seatings.forEach(s => {
              coversMerged += s.covers || 0
              resCountMerged += s.resCount || 0
              waitlistedMerged += s.waitlistCovers || 0
              if (s.blockReason || s.disabled) hasBlocked = true
              if (!s.disabled) hasEnabled = true
              if (s.mealPeriodId === mp.id) {
                resCount += s.resCount || 0
                covers += s.covers || 0
                waitlisted += s.waitlistCovers || 0
                hide = false
              } else {
                overlaps = true
              }
              overlapIds[s.mealPeriodId] = true
            })
            // console.log(e.name, maxSeating, memberMax, covers)
            mp._overlapIds = Object.keys(overlapIds)
            mp._coversMerged = coversMerged
            mp._resCountMerged = resCountMerged
            mp._covers = covers
            mp._resCount = resCount
            mp._waitlisted = waitlisted
            mp._waitlistedMerged = waitlistedMerged
            mp._fullyBlocked = !hasEnabled
            mp._hasBlocked = hasBlocked
            mp._hide = hide
            mp._overlaps = overlaps
          })
          this.dailySummary = mealPeriods
          // if (mpList.length > 0) this.$ftAPI().get(this.addResourceErrorHandler(this.reservationCountsCommand(mpList)))
        },
        params: {
          include_disabled: false,
          include_fields: ['locationName'],
          target_date: this.date,
          order_by: ['locationName', 'startTime'],
          filter_methods: {mergeSeatings: [true]},
        }
      }
    },
    upcomingEventsCommand (range) {
      // console.log('upcomingEventsCommand running in ', range)
      return {
        command: 'diningEvent',
        load: (events) => {
          // console.log('Loaded ' + events.length + ' events:', events)
          events.forEach(e => {
            let maxSeating = 0
            let memberMax = 0
            let covers = 0
            let waitlisted = 0
            e.fullSeatings.forEach(fs => {
              const sm = this.getSeatingMax(fs)
              maxSeating += sm.max
              memberMax += sm.memberMax
              covers += fs.covers || 0
              waitlisted += fs.waitlistCovers || 0
            })
            // console.log(e.name, maxSeating, memberMax, covers)
            e._maxSeating = maxSeating
            e._memberMax = memberMax
            e._covers = covers
            e._waitlisted = waitlisted
            e._memberCuttoff = covers >= memberMax // we can use this to visualy indicate members can not longer book
          })
          this.upcomingEvents = events
        },
        loading: this.setIsLoading,
        params: {
          children: true,
          filter: this.eventsFilter(range),
          include_fields: ['fullSeatings']
        }
      }
    },
    todaysEventsCommand () {
      return {
        command: 'diningEvent',
        load: (events) => {
          // events = this.computeSeatings(events)
          events.forEach(e => {
            let maxSeating = 0
            let memberMax = 0
            let covers = 0
            let waitlisted = 0
            e.fullSeatings.forEach(fs => {
              const sm = this.getSeatingMax(fs)
              maxSeating += sm.max
              memberMax += sm.memberMax
              covers += fs.covers || 0
              waitlisted += fs.waitlistCovers || 0
            })
            // console.log(e.name, maxSeating, memberMax, covers)
            e._maxSeating = maxSeating
            e._memberMax = memberMax
            e._covers = covers
            e._waitlisted = waitlisted
            e._memberCuttoff = covers >= memberMax // we can use this to visualy indicate members can not longer book
          })
          this.todaysEvents = events
        },
        loading: this.setIsLoading,
        params: {
          children: true,
          filter: [{field: 'date', operator: 'EQUAL', values: [this.date]}],
          include_fields: ['fullSeatings']
        }
      }
    },
    loadData (excludeUpcomingEvents) {
      this.dateDisplay = moment(this.date, 'YYYY-MM-DD').format('MMMM Do YYYY')
      const clubMoment = this.$ftAPI().clubMoment()
      this.reservationAllowStart = clubMoment.clone().subtract(parseInt(this.$ftCS.DINING_PAST_RES_MODIFY_DAYS, 10), 'days')
      this.eventAllowStart = clubMoment.clone().subtract(parseInt(this.$ftCS.DINING_PAST_EVENT_MODIFY_DAYS, 10), 'days')
      this.reservationAllowEnd = clubMoment.clone().add(parseInt(this.$ftCS.DINING_MAX_DAYS_IN_ADV_STAFF, 10), 'days')
      const commands = [
        {command: 'diningAvailableMealPeriodDates', load: this.availableMealPeriodDates, loading: this.isLoading},
        ...excludeUpcomingEvents ? [] : [this.upcomingEventsCommand(this.range)],
        this.todaysEventsCommand(),
        this.dailySummaryCommand()
      ]
      if (this.selectedEventId) commands.push(...this.eventSummaryCommand(this.selectedEventId)); else this.eventsWithSeatings = []
      if (this.selectedMealPeriodId) commands.push(this.mealPeriodSummaryCommand(this.selectedMealPeriodId)); else this.mealPeriodsWithSeatings = []
      this.$ftAPI().get(this.addResourceErrorHandler(commands))
      this.scheduleNextRefresh()
    },
    periodCountsByPartySize (seating, partySize) {
      partySize = partySize || 1
      // Return cache if we have any
      if (!seating._pcLookup) seating._pcLookup = {}
      if (seating._pcLookup[partySize]) return seating._pcLookup[partySize]
      const periodCounts = seating.periodCounts || {}
      let ic = 1
      for (let i in periodCounts) {
        let ii = parseInt(i, 10)
        if (ii > partySize) break
        if (ii > ic) {
          ic = ii
        }
      }
      const result = periodCounts[ic] || {}
      seating._pcLookup[partySize] = result
      return result
    },
  }
}
</script>
<style>
.ft-dashboard-section-label {
  font-size: 16px;
  font-weight: bold;
}
.ft-dashboard-section-header {
  font-size: 13px;
  font-weight: bold;
}
table.ft-dashboard-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 14px
}
table.ft-dashboard-table thead tr th {
  font-size: 13px;
  font-weight: bold;
  text-align: left;
  color: #FFF;
  padding: 2px 4px
}
table.ft-dashboard-table tbody tr:nth-child(even) {
  background: rgb(236, 244, 253);
}
table.ft-dashboard-table tbody tr:nth-child(odd) {
  background: #FFF;
}
.theme--dark table.ft-dashboard-table tbody tr:nth-child(even) {
  background: rgb(56, 57, 59);
}
.theme--dark table.ft-dashboard-table tbody tr:nth-child(odd) {
  background: rgb(51, 51, 51);
}
table.ft-dashboard-table tbody tr.ftIsSelected td {
  background: rgba(0, 0, 0, 0.10);
}
table.ft-dashboard-table tbody tr td {
  font-size: 12px;
  font-weight: normal;
  padding: 2px 4px
}
table.ft-dashboard-table tbody td:nth-last-child(n+2) {
  border-right: 1px solid rgb(207, 207, 207);
}
.theme--dark table.ft-dashboard-table tbody td:nth-last-child(n+2) {
  border-right: 1px solid rgb(73, 73, 73);
}
table.ft-dashboard-table .ftDateSpans span {
  white-space: nowrap;
}
.ft-dashboard-options {
  font-size: 10px;
  font-weight: normal;
}
.ft-dashboard-options a {
  margin: 0px 4px
}
</style>
